import React from "react"
import CreatePurchaseModal from "./create_purchase_modal_container"
import { fetchVirtualSuiteTypes, fetchCategories as apiFetchCategories } from "../../util/virtual_staging_api_util"
import { stripFileExtension } from "../../util/easy_format_utils"
import ScrollArrows from "../scroll_arrows"

export default class VirtualStagingOrderModal extends React.Component {
  constructor(props) {
    super(props)

    const account_credit =
      (this.props.project.client?.firm?.broker_charge && this.props.project.client?.account_credit) ||
      this.props.currentUser.roles?.broker
        ? null
        : this.props.project.client?.account_credit?.amount

    this.state = {
      step: 1,
      selectedPhotoIds: [],
      configs: {},
      paymentModalOpen: false,
      account_credit,
      suiteTypes: [],
      categoryOptions: {},
      submitErrors: [],
      incompletePhotoIds: [],
      showExitConfirm: false,
      submitted: false,
      photosLoading: true,
    }

    this.goToStep2 = this.goToStep2.bind(this)
    this.goToStep3 = this.goToStep3.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.togglePhoto = this.togglePhoto.bind(this)

    this.galleryScrollRef = React.createRef()
    this.configureScrollRef = React.createRef()
    this.summaryScrollRef = React.createRef()
  }

  componentDidMount() {
    // When nested inside DashboardVirtualStagingModal (identified by onBack),
    // that parent already owns the scroll lock for the whole flow — don't touch it here,
    // or unmounting this step (e.g. clicking "Back") would unlock scrolling under the parent modal.
    if (!this.props.onBack) document.body.style.overflow = "hidden"
    this.props.fetchVirtualStagingInfo(this.props.project.id).then(() => this.setState({ photosLoading: false }))

    fetchVirtualSuiteTypes().then(types => {
      this.setState({ suiteTypes: types })
      // Pre-fetch categories for every type so they're ready by step 2
      types.forEach(type => {
        apiFetchCategories(type.id).then(cats => {
          const catArray = Object.values(cats).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          this.setState(prev => ({
            categoryOptions: { ...prev.categoryOptions, [type.id]: catArray }
          }))
        })
      })
    })
  }

  componentWillUnmount() {
    if (!this.props.onBack) document.body.style.overflow = "unset"
  }

  togglePhoto(photoId) {
    const { selectedPhotoIds } = this.state
    if (selectedPhotoIds.includes(photoId)) {
      this.setState({ selectedPhotoIds: selectedPhotoIds.filter(id => id !== photoId) })
    } else {
      this.setState({ selectedPhotoIds: [...selectedPhotoIds, photoId] })
    }
  }

  skipsPayment() {
    const { project, currentUser } = this.props
    const clientRole = project.client?.role?.name
    return (
      clientRole === "admin" ||
      clientRole === "broker" ||
      currentUser?.roles?.broker ||
      (clientRole === "sub_broker" && project.client?.firm?.broker_charge_virtual_staging)
    )
  }

  categoriesForType(suiteTypeId) {
    return this.state.categoryOptions[suiteTypeId] || []
  }

  goToStep2() {
    const { selectedPhotoIds, configs, suiteTypes } = this.state

    const vsType = suiteTypes.find(t => t.name?.toLowerCase().includes("virtual staging"))
    const vsTypeId = vsType?.id?.toString() || null
    const vsCats = vsTypeId ? this.categoriesForType(vsTypeId) : []

    const newConfigs = {}
    selectedPhotoIds.forEach(photoId => {
      if (configs[photoId]) {
        newConfigs[photoId] = configs[photoId]
      } else {
        newConfigs[photoId] = {
          suiteTypeId: vsTypeId,
          categoryId: vsCats.length === 1 ? vsCats[0].id : null,
          productId: vsCats.length === 1 ? (vsCats[0].default_product_id || null) : null,
          bullets: [],
          currentNote: "",
        }
      }
    })
    this.setState({ step: 2, configs: newConfigs })
  }

  goToStep3() {
    this.setState({ step: 3 })
  }

  handleSuiteTypeChange(photoId, suiteTypeId) {
    const cats = this.categoriesForType(suiteTypeId)
    const { configs } = this.state
    this.setState({
      configs: {
        ...configs,
        [photoId]: {
          ...configs[photoId],
          suiteTypeId,
          categoryId: cats.length === 1 ? cats[0].id : null,
          productId: cats.length === 1 ? (cats[0].default_product_id || null) : null,
        }
      }
    })
  }

  updateConfig(photoId, field, value) {
    const { configs } = this.state
    const updated = { ...configs[photoId], [field]: value }

    if (field === "categoryId") {
      const cats = this.categoriesForType(configs[photoId]?.suiteTypeId)
      const category = cats.find(c => c.id === parseInt(value))
      updated.productId = category?.default_product_id || null
    }

    this.setState({ configs: { ...configs, [photoId]: updated } })
  }

  totalPrice() {
    const { selectedPhotoIds, configs } = this.state
    let total = 0
    selectedPhotoIds.forEach(photoId => {
      const cfg = configs[photoId]
      if (!cfg || !cfg.suiteTypeId) return
      const cats = this.categoriesForType(cfg.suiteTypeId)
      if (cfg.categoryId) {
        const category = cats.find(c => c.id === parseInt(cfg.categoryId))
        total += parseFloat(category?.price) || 0
      } else if (cats.length > 0) {
        const prices = cats.map(c => Math.round((parseFloat(c.price) || 0) * 100))
        if (prices.every(p => p === prices[0])) total += prices[0] / 100
      }
    })
    return total
  }

  handleSubmit() {
    const { selectedPhotoIds, configs, account_credit } = this.state
    const { project, currentUser } = this.props

    let price = this.totalPrice()
    let coupon_info = null
    let discount = 0

    if (account_credit && price > 0) {
      if (account_credit >= price) {
        discount = price
        price = 0
      } else {
        discount = account_credit
        price = price - account_credit
      }
    }

    if (discount > 0 && account_credit) {
      coupon_info = {
        coupon_id: project.client?.account_credit?.id,
        coupon_amount: parseInt(account_credit) - discount,
        coupon_discount_amount: discount,
      }
    }

    // Group photos by category — one purchase per category
    const byCategory = {}
    selectedPhotoIds.forEach(photoId => {
      const cfg = configs[photoId] || {}
      const key = cfg.categoryId || "null"
      if (!byCategory[key]) byCategory[key] = { categoryId: cfg.categoryId, photos: [] }
      const noteText = (cfg.currentNote || "").trim()
      byCategory[key].photos.push({ photoId, notes: noteText })
    })

    const virtual_staging_purchases_attributes = Object.values(byCategory).map(({ categoryId, photos }) => ({
      project_id: project.id,
      virtual_staging_category_id: categoryId,
      virtual_staging_photos_attributes: photos.map(({ photoId, notes }) => ({
        media_id: photoId,
        notes,
      })),
    }))

    const order_batch = {
      coupon_info,
      project_id: project.id,
      virtual_staging_purchases_attributes,
      broker_booked: currentUser.roles?.broker || false,
    }

    this.props.createVirtualStagingPurchase(order_batch).then(() => {
      this.props.onReloadProject()
      if (discount > 0 && account_credit) {
        this.props.fetchCurrentUser(this.props.currentUser.id)
      }
      this.setState({ submitted: true, paymentModalOpen: false })
    }).catch(errors => {
      this.setState({
        paymentModalOpen: false,
        submitErrors: Array.isArray(errors) ? errors : ["Something went wrong. Please try again."],
      })
    })
  }

  stepTitle() {
    const titles = {
      1: "Select all images for virtual staging",
      2: "Customize your order",
      3: "Order Summary",
    }
    return titles[this.state.step] || ""
  }

  renderStep1() {
    const { photos } = this.props
    const { selectedPhotoIds } = this.state
    const count = selectedPhotoIds.length

    return (
      <div className="vsOrderScreen">
        {this.state.photosLoading && (
          <div className="vsOrderGalleryEmpty">Loading photos…</div>
        )}
        {!this.state.photosLoading && photos.length === 0 && (
          <div className="vsOrderGalleryEmpty">
            Your media is still in production. Please check back later.
          </div>
        )}
        {!this.state.photosLoading && photos.length > 0 && (
          <div className="scrollEdgeWrapper">
            <div className="vsOrderGalleryGrid" ref={this.galleryScrollRef}>
              {photos.map(photo => {
                const selected = selectedPhotoIds.includes(photo.id)
                return (
                  <div
                    key={photo.id}
                    className={`vsOrderPhotoCard ${selected ? "vsOrderPhotoCardSelected" : ""}`}
                    onClick={() => this.togglePhoto(photo.id)}
                  >
                    <img src={photo.url} alt={photo.filename} className="vsOrderPhotoThumb" />
                    {selected && <div className="vsOrderPhotoCheck"><i className="fas fa-check" /></div>}
                    <div className="vsOrderPhotoName">{stripFileExtension(photo.filename)}</div>
                  </div>
                )
              })}
            </div>
            <ScrollArrows scrollRef={this.galleryScrollRef} />
          </div>
        )}
        <div className="vsOrderFooter">
          {this.props.onBack
            ? <button className="projectEditButton projectEditButtonLight vsOrderFooterBtn" onClick={this.props.onBack}>← Back</button>
            : <div className="vsOrderFooterSpacer" />}
          <button
            className="projectEditButton projectEditButtonPrimary vsOrderFooterBtn"
            onClick={() => { if (count > 0) this.goToStep2() }}
            style={{ opacity: count === 0 ? 0.4 : 1, cursor: count === 0 ? 'default' : 'pointer' }}
          >
            Next →
          </button>
        </div>
      </div>
    )
  }

  renderStep2() {
    const { selectedPhotoIds, configs, suiteTypes, incompletePhotoIds } = this.state
    const { photos } = this.props
    const total = this.totalPrice()

    return (
      <div className="vsOrderScreen">
        <div className="scrollEdgeWrapper">
        <div className="vsOrderList" ref={this.configureScrollRef}>
          {selectedPhotoIds.map(photoId => {
            const photo = photos.find(p => p.id === photoId)
            if (!photo) return null
            const cfg = configs[photoId] || {}
            const filteredCategories = this.categoriesForType(cfg.suiteTypeId)
            const showCategoryDropdown = filteredCategories.length > 1
            const selectedCategory = cfg.categoryId
              ? filteredCategories.find(c => c.id === parseInt(cfg.categoryId))
              : filteredCategories.length === 1 ? filteredCategories[0] : null
            const isIncomplete = incompletePhotoIds.includes(photoId)
            const missingSuiteType = isIncomplete && !cfg.suiteTypeId
            const missingCategory = isIncomplete && showCategoryDropdown && !cfg.categoryId

            return (
              <div key={photoId} className="vsOrderRow">
                <div className="vsOrderRowThumbWrap">
                  <img src={photo.url} alt={photo.filename} className="vsOrderPhotoThumb" />
                </div>
                <div className="vsOrderRowControls">
                  <select
                    value={cfg.suiteTypeId || ""}
                    onChange={e => {
                      this.setState({ incompletePhotoIds: incompletePhotoIds.filter(id => id !== photoId) })
                      this.handleSuiteTypeChange(photoId, e.target.value)
                    }}
                    className={`vsOrderSelect${missingSuiteType ? " vsOrderSelectError" : ""}`}
                  >
                    <option value="" disabled>Select type…</option>
                    {suiteTypes.filter(t => this.categoriesForType(t.id).length > 0).map(t => {
                      const cats = this.categoriesForType(t.id)
                      const prices = cats.map(c => parseFloat(c.price) || 0)
                      const allSame = prices.every(p => p === prices[0])
                      const priceLabel = allSame ? ` ($${prices[0]})` : ` ($${Math.min(...prices)})`
                      return <option key={t.id} value={t.id}>{t.name}{priceLabel}</option>
                    })}
                  </select>
                  <select
                    value={cfg.categoryId || ""}
                    onChange={e => {
                      this.setState({ incompletePhotoIds: incompletePhotoIds.filter(id => id !== photoId) })
                      this.updateConfig(photoId, "categoryId", e.target.value)
                    }}
                    className={`vsOrderSelect${missingCategory ? " vsOrderSelectError" : ""}`}
                    style={{ visibility: showCategoryDropdown ? "visible" : "hidden", order: showCategoryDropdown ? 0 : 99 }}
                  >
                    <option value="" disabled>Select room type…</option>
                    {(() => {
                      const prices = filteredCategories.map(c => parseFloat(c.price) || 0)
                      const allSame = prices.every(p => p === prices[0])
                      return filteredCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {allSame ? cat.name : `${cat.name} ($${cat.price})`}
                        </option>
                      ))
                    })()}
                  </select>
                  <textarea
                    className="vsOrderNotesInput"
                    placeholder="Add instructions..."
                    value={cfg.currentNote || ""}
                    rows={Math.max((cfg.currentNote || "").split("\n").length, 1)}
                    onFocus={() => {
                      if (!cfg.currentNote) this.updateConfig(photoId, "currentNote", "• ")
                    }}
                    onChange={e => this.updateConfig(photoId, "currentNote", e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        this.updateConfig(photoId, "currentNote", (cfg.currentNote || "") + "\n• ")
                      }
                    }}
                  />
                  <div className="vsOrderRowPrice">${parseFloat(selectedCategory?.price) || 0}</div>
                </div>
              </div>
            )
          })}
        </div>
        <ScrollArrows scrollRef={this.configureScrollRef} />
        </div>
        <div className="vsOrderFooter">
          <button className="projectEditButton projectEditButtonLight vsOrderFooterBtn" onClick={() => this.setState({ step: 1 })}>← Stage More</button>
          <div className="vsOrderSubtotal">Total: ${total}</div>
          <button
            className="projectEditButton projectEditButtonPrimary vsOrderFooterBtn"
            onClick={() => {
              const incomplete = selectedPhotoIds.filter(id => !configs[id]?.suiteTypeId || !configs[id]?.categoryId)
              if (incomplete.length > 0) {
                this.setState({ incompletePhotoIds: incomplete })
              } else {
                this.setState({ incompletePhotoIds: [] })
                this.goToStep3()
              }
            }}
            style={{ opacity: selectedPhotoIds.every(id => configs[id]?.suiteTypeId && configs[id]?.categoryId) ? 1 : 0.4, cursor: selectedPhotoIds.every(id => configs[id]?.suiteTypeId && configs[id]?.categoryId) ? 'pointer' : 'default' }}
          >Review order →</button>
        </div>
      </div>
    )
  }

  renderStep3() {
    const { selectedPhotoIds, configs, suiteTypes } = this.state
    const { photos } = this.props
    const total = this.totalPrice()

    // Group photos by category for the summary
    const byCategory = {}
    selectedPhotoIds.forEach(photoId => {
      const cfg = configs[photoId] || {}
      const key = cfg.categoryId || "uncategorized"
      if (!byCategory[key]) {
        const cats = this.categoriesForType(cfg.suiteTypeId)
        const category = cats.find(c => c.id === parseInt(cfg.categoryId)) || (cats.length === 1 ? cats[0] : null)
        const suiteType = suiteTypes.find(t => t.id === parseInt(cfg.suiteTypeId))
        byCategory[key] = { suiteType, category, count: 0, showCategoryName: cats.length > 1 }
      }
      byCategory[key].count++
    })

    return (
      <div className="vsOrderScreen">
        <div className="scrollEdgeWrapper">
        <div className="vsOrderSummaryList" ref={this.summaryScrollRef}>
{Object.values(byCategory).map(({ suiteType, category, count, showCategoryName }, i) => (
            <div key={i} className="vsOrderSummaryRow">
              <div className="vsOrderSummaryInfo">
                <div className="vsOrderSummaryService">{suiteType?.name}{showCategoryName && category ? ` — ${category.name}` : ""}</div>
                <div className="vsOrderSummaryDetail">{count} photo{count !== 1 ? "s" : ""}</div>
              </div>
              <div className="vsOrderSummaryPrice">${((parseFloat(category?.price) || 0) * count).toFixed(2)}</div>
            </div>
          ))}
        </div>
        <ScrollArrows scrollRef={this.summaryScrollRef} />
        </div>
        <div className="vsOrderFooter">
          <button className="projectEditButton projectEditButtonLight vsOrderFooterBtn" onClick={() => this.setState({ step: 2 })}>← Back</button>
          <div className="vsOrderSubtotal">Total: ${total}</div>
          <button
            className="projectEditButton projectEditButtonPrimary vsOrderFooterBtn"
            onClick={() => this.setState({ paymentModalOpen: true })}
          >
            {this.skipsPayment() ? "Submit Order" : "Checkout →"}
          </button>
        </div>
      </div>
    )
  }

  render() {
    const { step, paymentModalOpen, showExitConfirm, submitted } = this.state
    const { project, onClose } = this.props

    if (submitted) {
      return (
        <div className="modal-background vsOrderModalBg">
          <div className="orderpreviewModal vsOrderModal vsCongratsModal" onClick={e => e.stopPropagation()}>
            <div className="orderPreviewModalComponent congratsModalComponent">
              <div className="congratsModalHeading">Congrats your virtual staging has been submitted!</div>
              <div className="congratsButtons">
                <button className="congratsViewListingButton" onClick={onClose}>Close</button>
                <button className="congratsViewDashboardButton" onClick={() => { onClose(); this.props.history?.push(`/projects_index/${project.slug}`) }}>View Project</button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (paymentModalOpen) {
      return (
        <CreatePurchaseModal
          submitOrder={this.handleSubmit}
          project={project}
          changeParentState={s => this.setState(s)}
          total={this.totalPrice()}
        />
      )
    }

    return (
      <div className="modal-background vsOrderModalBg">
        <div className="orderpreviewModal vsOrderModal" onClick={e => e.stopPropagation()}>
          <div className="orderPreviewModalComponent">
            {showExitConfirm ? (
              <div key="exit" className="vsExitConfirm">
                <div className="vsExitConfirmText">Are you sure you want to exit? Progress will not be saved.</div>
                <div className="vsExitConfirmButtons">
                  <button className="projectEditButton projectEditButtonLight vsOrderFooterBtn" onClick={onClose}>Yes</button>
                  <button className="projectEditButton projectEditButtonPrimary vsOrderFooterBtn" onClick={() => this.setState({ showExitConfirm: false })}>No</button>
                </div>
              </div>
            ) : (
              <div key="content" className="vsOrderStepContent">
                <div className="contractorModalHeading">
                  <div className="contractorModalDescription">{this.stepTitle()}</div>
                  <div className="closeIcon" style={{fontSize: "20px"}} onClick={() => this.setState({ showExitConfirm: true })}><i className="fas fa-times"></i></div>
                </div>
                {step === 1 && this.renderStep1()}
                {step === 2 && this.renderStep2()}
                {step === 3 && this.renderStep3()}
                {this.state.submitErrors.length > 0 && (
                  <ul className="virtualStagingErrors errors mt-2 mb-0">
                    {this.state.submitErrors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
}
